import { FileSearchCorner, Lightbulb } from 'lucide-react';
import React from 'react';

import { getCallSourceId } from '../../utils/evidence';
import type { FieldEvidence } from '../fields/types';
import { SimpleTooltipV2 } from './tooltip';

export interface ReviewButtonProps {
  sourceCallId?: string;
  evidence?: FieldEvidence[];
  onNavigateToCall: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  /** Optional handler invoked when the latest evidence is audit-sourced (source_type === 'ai_audit'). */
  onNavigateToAudit?: (auditId: string) => void;
  className?: string;
}

export const ReviewButton: React.FC<ReviewButtonProps> = ({
  sourceCallId,
  evidence,
  onNavigateToCall,
  onNavigateToAudit,
  className = '',
}) => {
  if (!(sourceCallId || evidence?.length) || !onNavigateToCall) return null;
  return (
    <>
      <span className="text-muted-foreground/40">|</span>
      <button
        type="button"
        aria-label="Review"
        onClick={e => {
          e.stopPropagation();
          // Discriminate on source_type (BI-865). Audit evidence routes to the
          // AI Audit tab; everything else is a call. getCallSourceId handles
          // the legacy source_call_id fallback (pre-BI-865 evidence in
          // extracted_data and unmigrated output_data rows).
          //
          // Anchor = last item in the evidence list, which stays correct across
          // every shape a field's evidence can have:
          //   - Written since BI-1747: exactly ONE entry. Call-extraction
          //     writers (bvagent _merge_evidence_for_call, hermes
          //     mergeEvidenceLeaf) now REPLACE a field's evidence with the
          //     contributing call's rather than appending, so the sole entry
          //     belongs to the call that won the field's value.
          //   - Written before BI-1747: one entry per call that touched the
          //     field, oldest first — the tail is the most recent. Those records
          //     were not migrated; they collapse to one entry the next time a
          //     call contributes to the field.
          //   - Audit-corrected since BI-1747: also exactly ONE entry.
          //     hermes ApplyAuditCorrections replaces the field's evidence with
          //     its own {source_type: 'ai_audit'} entry, which now also carries
          //     a `reasoning` (the auditor's rationale) — so the tooltip below
          //     works on corrected fields, where it used to go blank.
          //   - Audit-corrected before BI-1747: the audit entry was APPENDED, so
          //     it is the tail of a longer list. Same routing either way.
          const anchor = evidence?.[evidence.length - 1];
          if (anchor?.source_type === 'ai_audit' && anchor.source_id && onNavigateToAudit) {
            onNavigateToAudit(anchor.source_id);
            return;
          }
          const targetCallId = getCallSourceId(anchor) ?? sourceCallId ?? '';
          onNavigateToCall(targetCallId, anchor?.time_start, anchor?.time_end);
        }}
        className={`text-blue-500 hover:text-blue-600 cursor-pointer shrink-0 ${className}`}
      >
        <FileSearchCorner className="h-3.5 w-3.5" />
      </button>
    </>
  );
};

export interface ReasoningTooltipProps {
  evidence?: FieldEvidence[];
  className?: string;
}

/**
 * Yellow lightbulb icon that surfaces the anchor evidence item's reasoning on
 * hover. Renders nothing when reasoning is absent or empty so legacy rows (no
 * reasoning written) stay visually clean.
 *
 * Anchor = last evidence item (matches the ReviewButton routing anchor; see the
 * shape note there). Since BI-1747 a field written by call extraction carries a
 * single entry, so this is that entry's reasoning.
 */
export const ReasoningTooltip: React.FC<ReasoningTooltipProps> = ({ evidence, className = '' }) => {
  const reasoning = evidence?.[evidence.length - 1]?.reasoning?.trim();
  if (!reasoning) return null;
  return (
    <SimpleTooltipV2
      // Cap the surface at 400px so long multi-sentence reasonings wrap instead
      // of stretching into a single wide line. SimpleTooltipV2's `className`
      // applies to the trigger wrapper, not the content surface — wrapping
      // `content` in a max-width div is the lightest-touch fix.
      content={<div className="max-w-[400px]">{reasoning}</div>}
      className={className}
    >
      <Lightbulb className="h-3.5 w-3.5 text-yellow-500 shrink-0" aria-hidden="true" />
    </SimpleTooltipV2>
  );
};
